import { Button } from "@/components/ui/button";
import { ArrowRight, Briefcase, Users, Zap, Calendar, Shield, Clock, Target, User, Upload, Bell, Plus, Search, Eye, CheckCircle, FileText, MessageCircle, UserPlus, Menu, X } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { Footer } from "@/components/Footer";
import { Toggle } from "@/components/ui/toggle";
import { useSEO } from "@/hooks/useSEO";
import { getSupabase } from "@/lib/supabaseClient";
import { computeDashboardDest } from "@/lib/computeDashboardDest";
import { getActingRole } from "@/lib/acting";
// import NomeraMainHero from '../components/NomeraMainHero'
import NomeraMainHeroRevamp from "@/components/NomeraMainHeroRevamp";
import PartnerStrip from "@/components/PartnerStrip";
import ProcessToggleSection from "@/components/ProcessToggleSection";

const healthcareSilhouette = "/lovable-uploads/3d4c9d21-603f-47a6-ae32-fcc39a921e23.png";

export default function Landing() {
  // build: cache-bust marker 2025-08-11
  const [scrolled, setScrolled] = useState(false);
  const [showDebugUI, setShowDebugUI] = useState(false);
  const navigate = useNavigate();

  // Option A: Redirect if role is known
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const supabase = getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return; // not logged in → stay

      // Check if user is admin first
      const { data: adminCheck, error: adminError } = await supabase
        .from('admin_users')
        .select('user_id')
        .eq('user_id', session.user.id)
        .single();
      
      const isAdminUser = !adminError && !!adminCheck;
      
      if (isAdminUser) {
        // Check if admin is acting as another role
        const acting = getActingRole();
        
        if (acting === 'employer') {
          console.log("[landing] Admin acting as employer, redirecting to employer dashboard");
          if (!cancelled) navigate('/employer/dashboard', { replace: true });
          return;
        }
        
        if (acting === 'job_seeker') {
          console.log("[landing] Admin acting as job_seeker, redirecting to job seeker dashboard");
          if (!cancelled) navigate('/job-seeker/dashboard', { replace: true });
          return;
        }
        
        console.log("[landing] Admin user detected, redirecting to admin overview");
        if (!cancelled) navigate("/admin/overview", { replace: true });
        return;
      }

      const { data: row, error } = await supabase
        .from("profiles")
        .select("role, onboarding_completed, onboarding_draft, verification_status, verification_status_new, profile_status")
        .eq("user_id", session.user.id)
        .maybeSingle();

      if (error) {
        console.warn("[landing] profile read failed:", error);
        return; // don't redirect on errors; keep landing passive
      }

      const role = row?.role && (row.role === "candidate" ? "job_seeker" : row.role);
      if (!role) return; // no role yet → stay on landing, don't force choose-role

      // Check onboarding completion status
      const isOnboardingIncomplete = !row?.onboarding_completed;
      // Use Phase 2 verification status, fallback to old field for backward compatibility
      const verificationStatus = row?.verification_status_new || row?.verification_status;
      const profileStatus = row?.profile_status;
      const hasSavedProgress = row?.onboarding_draft && Object.keys(row.onboarding_draft).length > 0;
      
      // Check if this is a fresh login (user just authenticated)
      // Look for authentication indicators in URL params or session storage
      const urlParams = new URLSearchParams(window.location.search);
      const isOAuthRedirect = urlParams.get('src') === 'oauth' || urlParams.get('code') || urlParams.get('state');
      const isAuthRedirect = urlParams.get('next') || urlParams.get('redirect');
      const hasAuthIntent = sessionStorage.getItem('login_intent_role') || sessionStorage.getItem('oauth_started');
      const isFreshLogin = isOAuthRedirect || isAuthRedirect || hasAuthIntent || !sessionStorage.getItem('hasVisitedLanding');
      
      if (isFreshLogin) {
        sessionStorage.setItem('hasVisitedLanding', 'true');
        // Clean up auth indicators after use
        sessionStorage.removeItem('login_intent_role');
        sessionStorage.removeItem('oauth_started');
      }

      // If onboarding is incomplete, check if user has saved progress
      if (isOnboardingIncomplete) {
        // Only redirect to onboarding if user has saved progress AND this is a fresh login
        // (not just navigation after Save and Exit)
        if (hasSavedProgress) {
          // Check for Save and Exit suppression markers
          const fromSave = sessionStorage.getItem('fromSaveAndExit') === '1';
          const justSavedAndExited = sessionStorage.getItem('justSavedAndExited') === 'true';
          const savedAt = Number(sessionStorage.getItem('saveExitAt') || 0);
          const suppressWindowMs = 300_000; // 5 minutes (increased window)
          const withinWindow = savedAt > 0 && Date.now() - savedAt < suppressWindowMs;
          const cookieMarker = document.cookie.includes('nh_save_exit=1');
          const urlSaved = new URLSearchParams(window.location.search).get('saved');
          const noRedirect = new URLSearchParams(window.location.search).get('noredir') === '1';
          
          // Also check if user just saved recently (within last 5 minutes)
          const savedAtDraft = row?.onboarding_draft?.saved_at;
          let recentlySaved = false;
          if (savedAtDraft) {
            const savedTime = new Date(savedAtDraft).getTime();
            const now = Date.now();
            recentlySaved = (now - savedTime) < suppressWindowMs;
          }
          
          console.log("[landing] Checking Save and Exit markers:", {
            fromSave,
            justSavedAndExited,
            savedAt,
            withinWindow,
            cookieMarker,
            urlSaved,
            noRedirect,
            recentlySaved,
            savedAtDraft
          });
          
          // Suppress auto-redirect after Save & Exit (honor once, then clear)
          if ((fromSave && withinWindow) || justSavedAndExited || cookieMarker || urlSaved || noRedirect || recentlySaved) {
            console.log("[landing] Suppressing auto-redirect after Save & Exit");
            // Honor one time, then clear
            sessionStorage.removeItem('fromSaveAndExit');
            sessionStorage.removeItem('saveExitAt');
            sessionStorage.removeItem('justSavedAndExited');
            return; // <-- do not redirect
          }
          
          // Only redirect to onboarding if this is a fresh login (not just navigation)
          if (isFreshLogin) {
            // This is a fresh login with saved progress, redirect to onboarding
            const dest = 
              role === "employer" ? "/employer/onboarding" :
              "/job-seeker/onboarding";
            
            console.log("[landing] User has saved progress from fresh login, redirecting to onboarding to continue:", dest);
            if (!cancelled) navigate(dest, { replace: true });
            return;
          } else {
            console.log("[landing] User has saved progress but not a fresh login, staying on home page");
            return;
          }
        }
        
        // If no saved progress, redirect to awaiting approval page
        const dest = 
          role === "employer" ? "/employer/awaiting-approval" :
          "/job-seeker/awaiting-approval";
        
        console.log("[landing] User has incomplete onboarding with no saved progress, redirecting to awaiting approval:", dest);
        if (!cancelled) navigate(dest, { replace: true });
        return;
      }

      // If onboarding is complete but verification is pending, go to awaiting approval
      // Handle both old and new verification status values
      const isPendingVerification = verificationStatus === 'pending' || 
                                   verificationStatus === 'pending_verification' ||
                                   profileStatus === 'pending_verification';
      
      if (isPendingVerification) {
        // Only redirect to awaiting approval if this is a fresh login (not just browsing)
        if (isFreshLogin) {
          const dest = 
            role === "employer" ? "/employer/awaiting-approval" :
            "/job-seeker/awaiting-approval";
          
          console.log("[landing] User has pending verification from fresh login, redirecting to awaiting approval:", dest);
          if (!cancelled) navigate(dest, { replace: true });
          return;
        } else {
          console.log("[landing] User has pending verification but not a fresh login, staying on home page");
          return;
        }
      }

      // Onboarding complete and verified → go to the right dashboard
      // Handle both old ('approved') and new ('good') verification status values
      const isVerified = verificationStatus === 'approved' || 
                        verificationStatus === 'good' ||
                        profileStatus === 'verified_good';
      
      if (isVerified) {
        // Only redirect to dashboard if this is a fresh login (not just browsing)
        if (isFreshLogin) {
          const dest =
            role === "admin" ? "/admin/overview" :
            role === "employer" ? "/employer/dashboard" :
            "/job-seeker/dashboard";

          console.log("[landing] User is verified from fresh login, redirecting to dashboard:", dest);
          if (!cancelled) navigate(dest, { replace: true });
          return;
        } else {
          console.log("[landing] User is verified but not a fresh login, staying on home page");
          return;
        }
      }
      
      // If verification has issues, go to awaiting approval
      const hasVerificationIssues = verificationStatus === 'rejected' || 
                                   verificationStatus === 'issue' ||
                                   profileStatus === 'verified_issue';
      
      if (hasVerificationIssues) {
        // Only redirect to awaiting approval if this is a fresh login (not just browsing)
        if (isFreshLogin) {
          const dest = 
            role === "employer" ? "/employer/awaiting-approval" :
            "/job-seeker/awaiting-approval";
          
          console.log("[landing] User has verification issues from fresh login, redirecting to awaiting approval:", dest);
          if (!cancelled) navigate(dest, { replace: true });
          return;
        } else {
          console.log("[landing] User has verification issues but not a fresh login, staying on home page");
          return;
        }
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  // Debug UI and other effects
  useEffect(() => {
    console.log("🚀 Landing page loaded successfully");
    console.log("🔍 Landing page auth state: passive (only redirects if role is known)");
    
    // Test redirect logging
    console.log("🔍 Testing redirect log:", (window as any).__redir_log);
    
    // Respect runtime debug toggle (query param or localStorage)
    try {
      const isProd = import.meta.env.MODE === 'production' || import.meta.env.PROD;
      if (isProd) {
        setShowDebugUI(false);
      } else {
        const params = new URLSearchParams(window.location.search);
        const qp = params.get('debug');
        if (qp === '1') localStorage.setItem('nxr_debug', '1');
        if (qp === '0') localStorage.removeItem('nxr_debug');
        setShowDebugUI(localStorage.getItem('nxr_debug') === '1');
      }
    } catch (e) {
      // noop
    }
  }, []); // Run once on mount only
  
  // Performance optimizations
  useEffect(() => {
    // Add CSS optimizations for smooth scrolling
    const style = document.createElement('style');
    style.textContent = `
      /* Smooth scrolling optimizations */
      html {
        scroll-behavior: smooth;
        -webkit-overflow-scrolling: touch;
      }
      
      /* Hardware acceleration for animations */
      .nxr-inview {
        will-change: transform, opacity;
        transform: translateZ(0);
        backface-visibility: hidden;
      }
      
      /* Prevent layout shift during animations */
      .nxr-inview * {
        will-change: transform, opacity;
      }
      
      /* Smooth transitions for all interactive elements */
      * {
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      }
      
      /* Optimize images for smooth loading */
      img {
        image-rendering: -webkit-optimize-contrast;
        image-rendering: crisp-edges;
      }
      
      /* Reduce motion for users who prefer it */
      @media (prefers-reduced-motion: reduce) {
        * {
          animation-duration: 0.01ms !important;
          animation-iteration-count: 1 !important;
          transition-duration: 0.01ms !important;
        }
      }
      
      /* Prevent layout shift */
      .landing-section {
        contain: layout style paint;
      }
      
      /* Optimize animations */
      .animate-fade-in {
        animation: fadeIn 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards;
      }
      
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(20px); }
        to { opacity: 1; transform: translateY(0); }
      }
      
      /* Mobile Responsiveness Enhancements - Subtle Fixes Only */
      @media (max-width: 768px) {
        /* Ensure proper spacing on mobile */
        .landing-section {
          padding: 2rem 1rem !important;
        }
        
        /* Mobile-optimized button sizing - touch-friendly */
        button, .btn {
          min-height: 44px !important;
        }
        
        /* Mobile grid adjustments - subtle */
        .grid {
          gap: 1rem !important;
        }
        
        /* Mobile image sizing - maintain aspect ratio */
        img {
          max-width: 100% !important;
          height: auto !important;
        }
        
        /* Mobile spacing adjustments - subtle */
        .space-y-6 > * + * {
          margin-top: 1.5rem !important;
        }
        
        .space-y-8 > * + * {
          margin-top: 2rem !important;
        }
      }
      
      /* Tablet Responsiveness */
      @media (min-width: 769px) and (max-width: 1024px) {
        .landing-section {
          padding: 3rem 2rem !important;
        }
      }
      
      /* Touch-friendly interactions */
      @media (hover: none) and (pointer: coarse) {
        button, .btn, a {
          min-height: 44px !important;
        }
      }
    `;
    document.head.appendChild(style);
    
    // Performance monitoring
    let frameCount = 0;
    let lastTime = performance.now();
    
    const measurePerformance = () => {
      frameCount++;
      const currentTime = performance.now();
      
      if (currentTime - lastTime >= 1000) {
        const fps = Math.round((frameCount * 1000) / (currentTime - lastTime));
        setScrollPerformance({ fps, lastFrameTime: currentTime });
        frameCount = 0;
        lastTime = currentTime;
      }
      
      requestAnimationFrame(measurePerformance);
    };
    
    requestAnimationFrame(measurePerformance);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  // Restored for interactive sections that toggle visuals/content
  const [activeRole, setActiveRole] = useState<'job-seeker' | 'employer'>('job-seeker');
  const [isSection3InView, setIsSection3InView] = useState(false);
  const section3Ref = useRef<HTMLElement | null>(null);
  const carouselRef = useRef<HTMLDivElement | null>(null);
  const firstCardRef = useRef<HTMLDivElement | null>(null);
  const autoSlideTimer = useRef<number | null>(null);
  const touchStartX = useRef<number | null>(null);
  const section5Ref = useRef<HTMLElement | null>(null);
  const [ctaLight, setCtaLight] = useState(0); // 0..1 brightness
  const [ctaWaving, setCtaWaving] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  // Performance monitoring
  const [scrollPerformance, setScrollPerformance] = useState({ fps: 60, lastFrameTime: 0 });
  // Positions tabs state
  const [activeCategory, setActiveCategory] = useState<'nursing' | 'allied' | 'diagnostic' | 'pharmacy' | 'admin' | null>('nursing');


  // Listen for hero CTA role intents (from NomeraMainHeroRevamp)
  useEffect(() => {
    const onRole = (e: any) => {
      const role = e?.detail;
      if (role === 'job-seeker' || role === 'employer') {
        setActiveRole(role);
        try { document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch {}
      }
    };
    window.addEventListener('how-role' as any, onRole);
    return () => window.removeEventListener('how-role' as any, onRole);
  }, []);


  useSEO({
    title: "Nomera Health – Healthcare Job Matching Platform",
    description: "Connect healthcare professionals with top facilities. Apply, manage documents, and schedule interviews in one place.",
    keywords: "nomera health, healthcare jobs, nursing jobs, medical careers, healthcare recruitment, travel nursing, healthcare staffing"
  });

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrolled(window.scrollY > 20);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);



  useEffect(() => {
    if (!section3Ref.current || isSection3InView) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Use requestAnimationFrame for smooth state updates
            requestAnimationFrame(() => {
              setIsSection3InView(true);
            });
            observer.disconnect();
          }
        });
      },
      { 
        threshold: 0.15,
        rootMargin: '50px 0px',
        root: null
      }
    );
    observer.observe(section3Ref.current);
    return () => observer.disconnect();
  }, [isSection3InView]);

  // Section 4 carousel auto-slide
  useEffect(() => {
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!carouselRef.current || reduce) return;
    
    const step = () => {
      if (!carouselRef.current) return;
      const el = carouselRef.current;
      const card = firstCardRef.current;
      const cardWidth = card ? card.offsetWidth : Math.min(320, el.clientWidth);
      const style = getComputedStyle(el);
      const gap = parseInt(style.columnGap || style.gap || '16', 10) || 16;
      const delta = cardWidth + gap;
      const maxScroll = el.scrollWidth - el.clientWidth;
      const nextLeft = el.scrollLeft + delta;
      
      // Use requestAnimationFrame for smoother auto-scrolling
      requestAnimationFrame(() => {
        el.scrollTo({ 
          left: nextLeft >= maxScroll - 2 ? 0 : nextLeft, 
          behavior: 'smooth' 
        });
      });
    };
    
    autoSlideTimer.current = window.setInterval(step, 4500);
    return () => { 
      if (autoSlideTimer.current) window.clearInterval(autoSlideTimer.current); 
    };
  }, []);

  const handlePrev = () => {
    if (!carouselRef.current) return;
    const el = carouselRef.current;
    const card = firstCardRef.current;
    const style = getComputedStyle(el);
    const gap = parseInt(style.columnGap || style.gap || '16', 10) || 16;
    const cardWidth = card ? card.offsetWidth : Math.min(320, el.clientWidth);
    
    // Use requestAnimationFrame for smoother scrolling
    requestAnimationFrame(() => {
      el.scrollTo({ 
        left: Math.max(0, el.scrollLeft - cardWidth - gap), 
        behavior: 'smooth' 
      });
    });
  };
  
  const handleNext = () => {
    if (!carouselRef.current) return;
    const el = carouselRef.current;
    const card = firstCardRef.current;
    const style = getComputedStyle(el);
    const gap = parseInt(style.columnGap || style.gap || '16', 10) || 16;
    const cardWidth = card ? card.offsetWidth : Math.min(320, el.clientWidth);
    
    // Use requestAnimationFrame for smoother scrolling
    requestAnimationFrame(() => {
      el.scrollTo({ 
        left: Math.min(el.scrollWidth, el.scrollLeft + cardWidth + gap), 
        behavior: 'smooth' 
      });
    });
  };

  // 🚨 EMERGENCY HANDLER FUNCTIONS (removed navigation)
  const handleRetryProfileCreation = async () => {
    console.log('🔄 Retrying profile creation');
    // Emergency options removed - Landing is passive
    // Trigger a page reload to retry
    window.location.reload();
  };

  const handleSignOut = async () => {
    console.log('🚪 Signing out user');
    // Import supabase client here
    const { supabase } = await import('@/integrations/supabase/client');
    await supabase.auth.signOut();
    // No navigation - let auth state handle it
  };



  // Section 5: scroll-responsive lamp brightness and apple wave
  useEffect(() => {
    const target = section5Ref.current;
    if (!target) return;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const onScroll = () => {
      const rect = target.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      // compute visibility ratio (0 to 1) as it enters viewport
      const visible = Math.min(1, Math.max(0, (vh - rect.top) / (rect.height + vh)));
      setCtaLight(visible);
      if (!reduce) setCtaWaving((prev) => (visible >= 0.9 ? true : prev && visible > 0.3));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Ambient background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-pulse-glow"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-secondary/5 rounded-full blur-3xl animate-pulse-glow" style={{ animationDelay: '1.5s' }}></div>
      </div>

      {/* Header (glassy, sticky) */}
      <header role="banner" className="sticky top-0 z-50">
        <div
          className={`relative backdrop-blur-md transition-all duration-300 ${
            scrolled
              ? 'shadow-[0_6px_20px_rgba(0,0,0,.06)]'
              : ''
          }`}
          style={{ backdropFilter: 'blur(8px)', backgroundColor: scrolled ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.75)' }}
        >
          <style>{`
            @keyframes nxr-logo-in { from { opacity:0; transform: scale(.98) } to { opacity:1; transform: scale(1) } }
            @keyframes nxr-logo-breathe { 0%,100% { transform: scale(1) } 50% { transform: scale(1.01) } }
            .nxr-logo { animation: nxr-logo-in .6s ease both, nxr-logo-breathe 6s ease-in-out infinite; }
            .nxr-navlink { position: relative; transition: color .2s ease; }
            .nxr-navlink:after { content:''; position:absolute; left:0; right:0; bottom:-4px; height:2px; background: linear-gradient(90deg, rgba(163,207,162,.6), rgba(155,143,193,.6)); transform: scaleX(0); transform-origin:left; transition: transform .25s ease, opacity .25s ease; opacity:0; }
            .nxr-navlink:hover:after, .nxr-navlink:focus-visible:after { transform: scaleX(1); opacity:1; }
            @media(prefers-reduced-motion:reduce){ .nxr-logo { animation:none !important } .nxr-navlink:after { transition: none } }
          `}</style>
          {/* ambient subtle blobs */}
          <div className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute -left-10 top-0 w-40 h-24 rounded-full blur-3xl" style={{ background:'rgba(163,207,162,.10)' }} />
            <div className="absolute -right-8 top-2 w-40 h-24 rounded-full blur-3xl" style={{ background:'rgba(155,143,193,.10)' }} />
          </div>
          {/* bottom soft glow */}
          <div className="pointer-events-none absolute left-0 right-0 -bottom-1 h-3" style={{ background:'linear-gradient(to bottom, rgba(0,0,0,.06), rgba(0,0,0,0))' }} />

          <div className="max-w-6xl mx-auto px-6 h-16 md:h-18 flex items-center justify-between text-neutral-800">
            {/* Left: Logo */}
            <Link to="/" className="flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40">
              <img
                src="/lovable-uploads/Favicon.png"
                alt="Nomera Health Logo" 
                className="w-7 h-7 rounded-lg nxr-logo"
              />
              <span className="text-base sm:text-lg font-bold tracking-tight">Nomera Health</span>
            </Link>

            {/* Center: Desktop Nav */}
            <nav className="hidden md:flex items-center justify-center flex-1" aria-label="Main">
              <ul className="flex items-center gap-6">
                <li><Link to="/healthcare-professionals" className="nxr-navlink hover:text-neutral-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40">For Professionals</Link></li>
                <li><Link to="/employers" className="nxr-navlink hover:text-neutral-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40">For Employers</Link></li>
                <li><Link to="/vision" className="nxr-navlink hover:text-neutral-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40">Vision</Link></li>
                <li><a href="https://affiliate.nomerahealth.com" className="nxr-navlink hover:text-neutral-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40" rel="noopener noreferrer">Affiliate Program</a></li>
              </ul>
            </nav>

            {/* Right: Secondary + CTA (desktop) */}
            <div className="hidden md:flex items-center gap-4">
              <span className="text-neutral-400 font-semibold pointer-events-none select-none px-3 py-2">Coming soon</span>
              <Button
                size="sm"
                disabled
                className="rounded-xl bg-[color:var(--nxr-green)]/40 text-white/70 font-semibold shadow-none pointer-events-none select-none px-6 py-2"
              >
                Coming soon
              </Button>
            </div>

            {/* Mobile: hamburger */}
            <button
              type="button"
              aria-label="Open menu"
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
              onClick={() => setIsMenuOpen((v) => !v)}
              className="md:hidden p-2 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile panel */}
          <div
            id="mobile-menu"
            className={`md:hidden origin-top transition-[max-height,opacity] duration-300 ${isMenuOpen ? 'max-h-[360px] opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}
          >
            <div className="px-6 pb-6 pt-2">
              <div className="rounded-xl border border-black/5 bg-white/80 supports-[backdrop-filter]:backdrop-blur-md p-4" style={{ backgroundImage:'radial-gradient(rgba(0,0,0,.04) 1px, transparent 1px)', backgroundSize:'6px 6px' }}>
                <nav aria-label="Main mobile">
                  <ul className="flex flex-col gap-3">
                    <li><Link to="/healthcare-professionals" onClick={() => setIsMenuOpen(false)} className="py-3 block nxr-navlink focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40">For Professionals</Link></li>
                    <li><Link to="/employers" onClick={() => setIsMenuOpen(false)} className="py-3 block nxr-navlink focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40">For Employers</Link></li>
                    <li><Link to="/vision" onClick={() => setIsMenuOpen(false)} className="py-3 block nxr-navlink focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40">Vision</Link></li>
                    <li><a href="https://affiliate.nomerahealth.com" onClick={() => setIsMenuOpen(false)} className="py-3 block nxr-navlink focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40" rel="noopener noreferrer">Affiliate Program</a></li>
                  </ul>
                </nav>
                <div className="mt-4 flex items-center gap-3">
                  <span className="text-neutral-400 font-semibold">Coming soon</span>
                  <Button
                    size="sm"
                    disabled
                    className="w-full rounded-xl bg-[color:var(--nxr-green)]/40 text-white/70 font-semibold shadow-none pointer-events-none select-none"
                  >
                    Coming soon
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* HERO */}

      {/* 🎉 OAuth Success Banner */}
      {false && (
        <div className="relative z-40 bg-green-50 border-b border-green-200">
          <div className="max-w-6xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <div>
                  <p className="text-green-800 font-medium">🎉 Welcome! Your account has been created successfully.</p>
                  <p className="text-green-600 text-sm">You can now complete your profile or navigate to other pages.</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    console.log('🔍 OAuth success - user can navigate manually');
                    // OAuth success banner removed - Landing is passive
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                >
                  Got it
                </button>
                <button
                  onClick={() => {
                    // OAuth success banner removed - Landing is passive
                  }}
                  className="text-green-600 hover:text-green-800 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Debug tooltip (hidden by default). Enable with ?debug=1 */}
      {showDebugUI && (
        <div className="fixed top-4 right-4 bg-black/80 text-white p-3 rounded text-xs z-50">
          <div>Debug State:</div>
          <div>Landing: Passive Mode</div>
          <div>Show Emergency: No (passive mode)</div>
        </div>
      )}

      {/* Emergency banner hidden unless debug UI is on */}
      {showDebugUI && (
        <div className="relative z-40 bg-orange-50 border-b border-orange-200">
          <div className="max-w-6xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">!</span>
                </div>
                <div>
                  <p className="text-orange-800 font-medium">🚨 Navigation Issue Detected</p>
                  <p className="text-orange-600 text-sm">If you're stuck on this page, use the buttons below to navigate.</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <p className="text-orange-600 text-sm">Use the navigation menu above to access sign-in options.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Emergency button hidden unless debug UI is on */}
      {showDebugUI && (
        <div className="fixed bottom-4 right-4 z-50">
          <button
            onClick={() => {
              console.log('🚨 Fallback emergency button clicked');
              // Emergency options removed - Landing is passive
            }}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium shadow-lg"
          >
            🚨 Emergency Options
          </button>
        </div>
      )}

      {/* Hero Section - Revamp */}
      <NomeraMainHeroRevamp primaryImg="/materials/smiling-nurse.jpg" secondaryImg="/materials/retro-desk.png" />

      {/* Trust / Market strip */}
      <style>{`
          .partner-strip{
            --tan:#fbf7ef;
          --green:rgba(104,168,120,0.10);
          --purple:rgba(163,142,208,0.10);
            background:
              radial-gradient(60rem 20rem at 10% 20%, var(--green), transparent 60%),
              radial-gradient(40rem 16rem at 90% 70%, var(--purple), transparent 60%),
              var(--tan);
          padding: clamp(1.5rem, 4vw, 2.5rem) 0 clamp(1rem, 3vw, 2rem);
        }
        .partner-inner{ max-width:min(1100px,90vw); margin:0 auto; padding:0 clamp(1rem,3vw,2rem); }
          .partner-marquee{
          position:relative; overflow:hidden; padding: clamp(0.75rem,2vw,1.5rem) 0;
            -webkit-mask-image: linear-gradient(to right, transparent 0, #000 8%, #000 92%, transparent 100%);
                    mask-image: linear-gradient(to right, transparent 0, #000 8%, #000 92%, transparent 100%);
          }
          .partner-track{
          display:flex; align-items:center; gap:clamp(32px,5vw,56px);
          animation:logos-scroll 25s linear infinite; list-style:none; padding:0; margin:0; width:max-content;
        }
        .partner-logo{ height:clamp(48px,8vw,80px); min-width:clamp(48px,8vw,80px); width:auto; object-fit:contain; display:block; transition:transform .2s ease; }
        .partner-logo:hover{ transform: translateY(-2px); }
        @keyframes logos-scroll{ from{ transform:translateX(0);} to{ transform:translateX(-50%);} }
        @media(prefers-reduced-motion:reduce){ .partner-track{ animation:none; } }
        @media (max-width:768px){
          .partner-marquee{ -webkit-mask-image:none !important; mask-image:none !important; }
          .partner-track{ will-change:auto; }
          .partner-logo{ height:56px !important; min-width:56px !important; }
        }
        @media (max-width:480px){
          .partner-track{ gap:24px; animation-duration:18s; }
          .partner-logo{ height:48px !important; min-width:48px !important; }
          }
        `}</style>
      <section className="partner-strip" aria-labelledby="serving-title">
        <div className="partner-inner">
          <p id="serving-title" className="text-base md:text-lg font-medium text-[#36424a] mb-3 text-center">
            Open To: <strong className="font-bold">Missouri</strong> • <strong className="font-bold">Kansas</strong>
          </p>
          <p className="text-xs md:text-sm text-[#8a8f9a] italic text-center mb-4 leading-relaxed opacity-80">
            No affiliation with these companies. Logos used for regional reference only.
          </p>

          <div className="partner-marquee" aria-label="Hospitals and health systems we serve">
            {/* The trick: two identical lists back-to-back for a perfect loop */}
            <ul className="partner-track">
              {/* Set A */}
              <li><img src="/Logos/01-hca-midwest-health.png" alt="HCA Midwest Health" className="partner-logo" /></li>
              <li><img src="/Logos/02-saint-lukes.png" alt="Saint Luke's" className="partner-logo" /></li>
              <li><img src="/Logos/03-sunflower-medical-group.png" alt="Sunflower Medical Group" className="partner-logo" /></li>
              <li><img src="/Logos/04-childrens-mercy-kansas-city.png" alt="Children's Mercy Kansas City" className="partner-logo" /></li>
              <li><img src="/Logos/05-stormont-vail-health.png" alt="Stormont Vail Health" className="partner-logo" /></li>
              <li><img src="/Logos/06-saint-joseph-hospital.png" alt="Saint Joseph Hospital" className="partner-logo" /></li>
              <li><img src="/Logos/07-clay-platte-family-medicine-clinic.png" alt="Clay Platte Family Medicine Clinic" className="partner-logo" /></li>
              <li><img src="/Logos/08-liberty-hospital.png" alt="Liberty Hospital" className="partner-logo" /></li>
              <li><img src="/Logos/09-kindred-hospital.png" alt="Kindred Hospital" className="partner-logo" /></li>
              <li><img src="/Logos/10-providence-medical-center.png" alt="Providence Medical Center" className="partner-logo" /></li>

              {/* Set B (identical copy, ensures no gap) */}
              <li><img src="/Logos/01-hca-midwest-health.png" alt="" className="partner-logo" aria-hidden="true" /></li>
              <li><img src="/Logos/02-saint-lukes.png" alt="" className="partner-logo" aria-hidden="true" /></li>
              <li><img src="/Logos/03-sunflower-medical-group.png" alt="" className="partner-logo" aria-hidden="true" /></li>
              <li><img src="/Logos/04-childrens-mercy-kansas-city.png" alt="" className="partner-logo" aria-hidden="true" /></li>
              <li><img src="/Logos/05-stormont-vail-health.png" alt="" className="partner-logo" aria-hidden="true" /></li>
              <li><img src="/Logos/06-saint-joseph-hospital.png" alt="" className="partner-logo" aria-hidden="true" /></li>
              <li><img src="/Logos/07-clay-platte-family-medicine-clinic.png" alt="" className="partner-logo" aria-hidden="true" /></li>
              <li><img src="/Logos/08-liberty-hospital.png" alt="" className="partner-logo" aria-hidden="true" /></li>
              <li><img src="/Logos/09-kindred-hospital.png" alt="" className="partner-logo" aria-hidden="true" /></li>
              <li><img src="/Logos/10-providence-medical-center.png" alt="" className="partner-logo" aria-hidden="true" /></li>
            </ul>
          </div>
        </div>
      </section>

      {/* Interactive Process Toggle Section - Revamp */}
      <section id="how-it-works" className="landing-section relative w-full py-20 overflow-hidden" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 100%)' }} data-mode={activeRole}>
        {/* Local styles for this section */}
        <style>{`
          :root{--nxr-green:#A3CFA2;--nxr-purple:#9B8FC1;--nxr-card:#FFFFFFF2;--nxr-gray-500:#6B7280}
          @keyframes nxr-fade-up{from{opacity:0; transform:translateY(8px)} to{opacity:1; transform:translateY(0)}}
          @keyframes nxr-crossfade{from{opacity:0} to{opacity:1}}
          @keyframes nxr-grow-line{from{stroke-dashoffset:220} to{stroke-dashoffset:0}}
          @keyframes nxr-drift{0%{transform:translate(0,0)}50%{transform:translate(10px,-6px)}100%{transform:translate(0,0)}}
          .nxr-step{animation:nxr-fade-up .5s ease both}
          .nxr-icon{transition:transform .25s ease, filter .25s ease}
          .nxr-card:hover .nxr-icon, .nxr-card:focus-within .nxr-icon{ transform:scale(1.07); }
          [data-mode="job-seeker"] .nxr-card:hover .nxr-icon, [data-mode="job-seeker"] .nxr-card:focus-within .nxr-icon{ filter: drop-shadow(0 0 10px rgba(163,207,162,.55)); }
          [data-mode="employer"] .nxr-card:hover .nxr-icon, [data-mode="employer"] .nxr-card:focus-within .nxr-icon{ filter: drop-shadow(0 0 10px rgba(155,143,193,.55)); }
          .nxr-tooltip{ display:none; }
          .nxr-card:hover .nxr-tooltip, .nxr-card:focus-within .nxr-tooltip{ display:block; }
          .nxr-bg-layer{ transition: opacity .35s ease, transform .35s ease; }
          .nxr-btn{ position: relative; overflow: hidden; }
          .nxr-btn:focus-visible{ outline: 3px solid rgba(0,0,0,.35); outline-offset: 3px; }
          .nxr-btn-candidate:hover::before{ content:''; position:absolute; inset:-20%; background:radial-gradient(circle at center, rgba(163,207,162,.22), transparent 60%); animation:ripple .5s ease forwards }
          @keyframes ripple{ from{ transform:scale(.8); opacity:.7 } to{ transform:scale(1.25); opacity:0 } }
          .nxr-btn-employer .nxr-arrow{ transition: transform .25s ease }
          .nxr-btn-employer:hover .nxr-arrow{ transform: translateX(6px) }
          @keyframes nxr-shimmer { 0% { opacity:.10; transform: translateX(-8%) } 50% { opacity:.20 } 100% { opacity:.10; transform: translateX(8%) } }
          @media(prefers-reduced-motion:reduce){ .nxr-step, .nxr-bg-layer{ animation:none !important; transition:none !important } }
        `}</style>

        {/* Ambient motifs for visual consistency */}
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute -left-12 top-6 w-80 h-80 rounded-full blur-3xl" style={{ background:'rgba(163,207,162,0.20)' }} />
          <div className="absolute -right-10 top-12 w-80 h-80 rounded-full blur-3xl" style={{ background:'rgba(155,143,193,.15)' }} />
          <div className="absolute left-0 right-0 top-1/2 h-px" style={{ background:'linear-gradient(90deg, rgba(163,207,162,0), rgba(163,207,162,0.4), rgba(155,143,193,0.4), rgba(155,143,193,0))', animation: 'nxr-shimmer 6s ease-in-out infinite' }} />
        </div>

        {/* Mode-aware decorative background */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Candidate background */}
          <div className={`nxr-bg-layer absolute inset-0 ${activeRole === 'job-seeker' ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2'}`} aria-hidden="true">
            <div className="absolute -left-10 -top-8 w-72 h-72 rounded-full" style={{ background:'radial-gradient(closest-side, rgba(163,207,162,.18), transparent 70%)'}} />
            <div className="absolute -right-10 bottom-0 w-64 h-64 rounded-full" style={{ background:'radial-gradient(closest-side, rgba(163,207,162,.14), transparent 70%)'}} />
            {/* corner vine */}
            <svg className="absolute left-0 top-8 w-56 h-24 opacity-25" viewBox="0 0 300 120" fill="none">
              <path d="M0 110 C 60 60, 140 100, 220 40" stroke="#7FB17D" strokeWidth="1.5" style={{ strokeDasharray:220, strokeDashoffset:220, animation:'nxr-grow-line 6s ease forwards' as unknown as string }} />
            </svg>
            {/* drifting leaves */}
            <div className="absolute right-[15%] top-[30%] w-4 h-4 bg-[color:var(--nxr-green)] rounded-full opacity-20" style={{ animation:'nxr-drift 10s linear infinite' as unknown as string }} />
            <div className="absolute left-[12%] bottom-[20%] w-3 h-3 bg-[color:var(--nxr-green)] rounded-full opacity-20" style={{ animation:'nxr-drift 12s linear infinite 2s' as unknown as string }} />
          </div>
          {/* Employer background */}
          <div className={`nxr-bg-layer absolute inset-0 ${activeRole === 'employer' ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'}`} aria-hidden="true">
            <div className="absolute -right-10 -top-8 w-72 h-72 rounded-full" style={{ background:'radial-gradient(closest-side, rgba(155,143,193,.18), transparent 70%)'}} />
            <div className="absolute -left-10 bottom-0 w-64 h-64 rounded-full" style={{ background:'radial-gradient(closest-side, rgba(155,143,193,.14), transparent 70%)'}} />
            {/* node lines */}
            <svg className="absolute right-4 top-8 w-56 h-28 opacity-25" viewBox="0 0 300 150" fill="none">
              <circle cx="20" cy="120" r="3" fill="#9B8FC1" />
              <circle cx="110" cy="90" r="3" fill="#9B8FC1" />
              <circle cx="200" cy="60" r="3" fill="#9B8FC1" />
              <circle cx="280" cy="30" r="3" fill="#9B8FC1" />
              <path d="M20 120 L110 90 L200 60 L280 30" stroke="#9B8FC1" strokeWidth="1.2" />
            </svg>
            {/* floating dots */}
            <div className="absolute right-[25%] top-[35%] w-2 h-2 bg-[color:var(--nxr-purple)] rounded-full opacity-25" style={{ animation:'nxr-drift 9s linear infinite' as unknown as string }} />
            <div className="absolute left-[18%] bottom-[22%] w-2 h-2 bg-[color:var(--nxr-purple)] rounded-full opacity-25" style={{ animation:'nxr-drift 11s linear infinite 2s' as unknown as string }} />
          </div>
        </div>

        <div className="relative mx-auto max-w-6xl px-6 z-10">
          {/* Eyebrow + Tagline */}
          <div className="text-center mb-8">
            <h2 className="text-sm tracking-widest uppercase text-neutral-600">Your Journey Starts Here</h2>
            <p className={`text-xl mt-2 ${activeRole === 'employer' ? 'text-[color:var(--nxr-purple)]' : 'text-[color:var(--nxr-green)]'}`} aria-live="polite">
              {activeRole === 'employer' ? (
                <span className="inline-block" style={{ animation:'nxr-crossfade .35s ease' as unknown as string }}>Build your dream team with confidence.</span>
              ) : (
                <span className="inline-block" style={{ animation:'nxr-crossfade .35s ease' as unknown as string }}>Step into your healthcare future.</span>
              )}
            </p>
          </div>
          
          {/* Toggle */}
          <div className="flex justify-center mb-8">
            <div className="bg-white/90 border border-black/10 rounded-full p-1 flex gap-1 shadow-sm">
                <button
                aria-pressed={activeRole === 'job-seeker'}
                  onClick={() => setActiveRole('job-seeker')}
                className={`px-6 py-3 rounded-full font-medium text-base transition-all duration-200 flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40 ${activeRole === 'job-seeker' ? 'bg-white text-[color:var(--nxr-green)] shadow-sm' : 'text-neutral-600 hover:bg-white'}`}
              >
                <Users className="w-4 h-4" /> Candidates
                </button>
                <button
                aria-pressed={activeRole === 'employer'}
                  onClick={() => setActiveRole('employer')}
                className={`px-6 py-3 rounded-full font-medium text-base transition-all duration-200 flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40 ${activeRole === 'employer' ? 'bg-white text-[color:var(--nxr-purple)] shadow-sm' : 'text-neutral-600 hover:bg-white'}`}
              >
                <Briefcase className="w-4 h-4" /> Employers
                </button>
              </div>
            </div>

          {/* Steps */}
          {(() => {
                          const candidate = [
                { icon: <UserPlus className="nxr-icon w-6 h-6" />, title: 'Sign Up & Profile', tip: 'Takes less than 2 minutes' },
                { icon: <Shield className="nxr-icon w-6 h-6" />, title: 'Verification', tip: "So employers know you're the real deal" },
                { icon: <Zap className="nxr-icon w-6 h-6" />, title: 'Get Matched', tip: 'We apply you to top matches' },
                { icon: <Bell className="nxr-icon w-6 h-6" />, title: 'Get Notified', tip: 'Be first to know when they reach out' },
              ];
            const employer = [
              { icon: <User className="nxr-icon w-6 h-6" />, title: 'Create Account', tip: 'Your hiring journey starts here' },
              { icon: <Plus className="nxr-icon w-6 h-6" />, title: 'Post a Job', tip: 'Find your next hire faster than ever' },
              { icon: <Search className="nxr-icon w-6 h-6" />, title: 'Review Matches', tip: 'Qualified, ready-to-work candidates' },
              { icon: <MessageCircle className="nxr-icon w-6 h-6" />, title: 'Interview & Hire', tip: 'Confident, data-informed decisions' },
            ];
            const steps = activeRole === 'employer' ? employer : candidate;
            return (
            <div className="relative">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {steps.map((s, i) => (
                    <div key={s.title} className="nxr-step nxr-card rounded-2xl border border-neutral-200 bg-white/85 shadow-sm backdrop-blur p-6 text-center focus-within:ring-2 focus-within:ring-black/10" style={{ animationDelay: `${i*0.15}s` }} tabIndex={0}>
                    <div className="flex items-center justify-center mb-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${activeRole==='employer' ? 'text-[color:var(--nxr-purple)]' : 'text-[color:var(--nxr-green)]'}`} style={{ background: activeRole==='employer' ? 'rgba(155,143,193,0.20)' : 'rgba(163,207,162,0.20)' }}>{s.icon}</div>
                      </div>
                      <h3 className="text-lg font-medium mb-2 text-neutral-900">{s.title}</h3>
                      <p className="text-sm text-[color:var(--nxr-gray-500)] leading-relaxed">{activeRole==='employer' ? (
                        s.title==='Create Account'? 'Create your employer account and set up your organization profile.':
                        s.title==='Post a Job'? 'Enter detailed job criteria, including role, requirements, and facility needs.':
                        s.title==='Review Matches'? 'Get a curated list of top-matched candidates with detailed profiles and compatibility scores.':
                        'Interview the best candidates and make data-driven hiring decisions with our integrated tools.'
                      ) : (
                        s.title==='Sign Up & Profile'? 'Create your account, add your credentials and experience, and upload your documents, certifications, and job preferences.':
                        s.title==='Verification'? 'We review and authenticate your credentials, and verify all submitted documents to ensure you are fully qualified.':
                        s.title==='Get Matched'? 'An algorithm enhanced with AI matches your skills, experience, location, and pay preferences to jobs in our system, connecting you with employers with no extra applications required.':
                        'Receive instant notifications when employers are interested and ready to move forward with interviews.'
                      )}</p>
                      <div className={`nxr-tooltip mt-3 text-xs ${activeRole==='employer' ? 'text-[color:var(--nxr-purple)]' : 'text-[color:var(--nxr-green)]'}`}>{s.tip}</div>
                    </div>
                  ))}
                  </div>
                        </div>
            );
          })()}

          {/* Mode-specific CTAs */}
          <div className="mt-8 flex flex-col items-center justify-center gap-2">
            {activeRole === 'employer' ? (
              <>
                <Link to="/auth?role=employer" aria-label="Find My Next Hire">
                  <Button size="lg" className="nxr-btn nxr-btn-employer bg-[color:var(--nxr-purple)] hover:bg-[#8e80c0] text-white font-semibold shadow-sm">Find My Next Hire <ArrowRight className="nxr-arrow ml-2 h-5 w-5" /></Button>
                </Link>
                <p className="mt-3 text-sm text-neutral-600">Only see qualified, ready-to-work talent.</p>
              </>
            ) : (
              <>
                <Link to="/auth?role=job_seeker" aria-label="Start My Journey">
                  <Button size="lg" className="nxr-btn nxr-btn-candidate bg-[color:var(--nxr-green)] hover:bg-[#96c395] text-neutral-900 font-semibold shadow-sm">Start My Journey</Button>
                </Link>
                <p className="mt-3 text-sm text-neutral-600">Your information is secure and private.</p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Section 3 – Mobile App Interface Mockup */}
      <section id="benefits" ref={section3Ref} className={`landing-section relative w-full py-10 md:py-12 overflow-hidden ${isSection3InView ? 'nxr-inview' : ''}`} style={{ background: 'linear-gradient(180deg, #FAF6F0 0%, #FFFFFF 72%)' }}>
        {/* Ambient blobs */}
        <div className="absolute inset-0 pointer-events-none -z-10">
          <div className="absolute -left-10 top-12 w-72 h-72 rounded-full blur-3xl" style={{ background:'rgba(163,207,162,.20)' }} />
          <div className="absolute -right-10 top-16 w-80 h-80 rounded-full blur-3xl" style={{ background:'rgba(155,143,193,.15)' }} />
        </div>

        <div className="relative mx-auto max-w-6xl px-4 md:px-6 z-10">
          <div className="grid grid-cols-1 gap-6 md:gap-8 lg:grid-cols-12 items-center">
            {/* Left: Image Display - Takes up more space */}
            <div className="lg:col-span-7 flex justify-center lg:justify-start">
              <img 
                src="/materials/matched-candidates-mock-two.png" 
                alt="Nomera Health matched candidates interface mockup" 
                width="800" height="420"
                loading="lazy" decoding="async"
                className="h-72 md:h-80 lg:h-[420px] w-auto object-contain drop-shadow"
              />
            </div>

            {/* Right: Nomera Health Description - Takes up remaining space */}
            <div className="lg:col-span-5 text-left">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#36424a] mb-3 md:mb-5">
                Qualified candidates only
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6 md:mb-8">
                Our candidates are fully licensed, actively seeking healthcare positions, and matched to your exact job requirements through Nomera Health's advanced AI matching system.
              </p>
              
              {/* Additional Benefits - Left aligned */}
              <div className="space-y-3 md:space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 md:w-6 md:h-6 bg-[#A3CFA2]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-[#A3CFA2]" />
                  </div>
                  <span className="text-sm md:text-base text-gray-700">Get only licensed, pre-vetted candidates that are ready to work</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 md:w-6 md:h-6 bg-[#A3CFA2]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-[#A3CFA2]" />
                  </div>
                  <span className="text-sm md:text-base text-gray-700">Skip job boards and staffing fees that drain your resources</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 md:w-6 md:h-6 bg-[#A3CFA2]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-[#A3CFA2]" />
                  </div>
                  <span className="text-sm md:text-base text-gray-700">Fill roles quicker and reduce costly backfill delays</span>
                </div>
              </div>
              
              {/* CTA Button */}
              <div className="mt-6">
                <Link to="/employers">
                  <Button size="lg" className="bg-[color:var(--nxr-green)] hover:bg-[#96c395] text-neutral-900 font-semibold px-8 py-3 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">Start Hiring Today<ArrowRight className="ml-2 h-5 w-5" /></Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4 – Reversed Layout: Text Left, Image Right */}
      <section className="landing-section relative w-full py-10 md:py-12 overflow-hidden" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #FAF6F0 100%)' }}>
        {/* Ambient blobs */}
        <div className="absolute inset-0 pointer-events-none -z-10">
          <div className="absolute -left-10 top-12 w-72 h-72 rounded-full blur-3xl" style={{ background:'rgba(155,143,193,.15)' }} />
          <div className="absolute -right-10 top-16 w-80 h-80 rounded-full blur-3xl" style={{ background:'rgba(163,207,162,.20)' }} />
        </div>

        <div className="relative mx-auto max-w-6xl px-4 md:px-6">
          <div className="grid grid-cols-1 gap-6 md:gap-8 lg:grid-cols-12 items-center">
            {/* Mobile: Image Above Text */}
            <div className="lg:hidden order-1 mb-6">
              <img 
                src="/materials/iphone-candidate-matched-job.png" 
                alt="Nomera Health platform interface showcase" 
                width="800" height="420" loading="lazy" decoding="async"
                className="w-full max-w-[400px] mx-auto h-auto object-contain drop-shadow"
              />
            </div>

            {/* Left: Text Content */}
            <div className="lg:col-span-5 text-left order-2 lg:order-1">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#36424a] mb-3 md:mb-5">
                Streamlined hiring
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6 md:mb-8">
                Our platform simplifies every step of your job search, from application to placement, so you land the role that truly aligns with your goals.
              </p>
              
              {/* Additional Benefits - Left aligned */}
              <div className="space-y-3 md:space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 md:w-6 md:h-6 bg-[#A3CFA2]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-[#A3CFA2]" />
                  </div>
                  <span className="text-sm md:text-base text-gray-700">One profile, endless job applications done for you</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 md:w-6 md:h-6 bg-[#A3CFA2]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-[#A3CFA2]" />
                  </div>
                  <span className="text-sm md:text-base text-gray-700">More interviews. More offers. More opportunities</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 md:w-6 md:h-6 bg-[#A3CFA2]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-[#A3CFA2]" />
                  </div>
                  <span className="text-sm md:text-base text-gray-700">Pick your pay, schedule, and the jobs that fit your career goals</span>
                </div>
              </div>
              
              <div className="mt-8">
                <Link to="/healthcare-professionals">
                  <Button size="lg" className="bg-[#A3CFA2] hover:bg-[#96c395] text-neutral-900 font-semibold px-8 py-3 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    Explore Our Platform
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Desktop: Image Right (Hidden on Mobile) */}
            <div className="hidden lg:flex lg:col-span-7 justify-end order-3 lg:order-2">
              <img 
                src="/materials/iphone-candidate-matched-job.png" 
                alt="Nomera Health platform interface showcase" 
                width="800" height="420" loading="lazy" decoding="async"
                className="h-72 md:h-80 lg:h-[420px] w-auto object-contain drop-shadow"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Section 5 – Final CTA: Your Next Chapter Starts Here (split with animated apple + lamp) */}
      <section
        id="final-cta"
        className="relative w-full py-16 overflow-hidden"
        style={{ background: 'linear-gradient(180deg, #FAF6F0 0%, #E7F3E8 100%)', ["--progress" as any]: ctaLight || 0 }}
        ref={(el) => { /* assign lazily to avoid TS mismatch */ (section5Ref as any).current = el; }}
        aria-labelledby="final-cta-title"
      >
        <style>{`
          @keyframes wavePulse { 0%,100%{ opacity: 0 } 50% { opacity: .9 } }
          .wave-lines { animation: wavePulse 1.6s ease-in-out infinite; }
        `}</style>
        {/* Ambient motifs behind content */}
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute -left-12 top-6 w-80 h-80 rounded-full blur-3xl" style={{ background: 'rgba(163,207,162,0.20)' }} />
          <div className="absolute -right-10 top-12 w-80 h-80 rounded-full blur-3xl" style={{ background: 'rgba(155,143,193,0.15)' }} />
          </div>
          
        <div className="relative mx-auto max-w-6xl px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Mobile: Image First, then Content */}
            {/* Desktop: Content Left, Image Right */}
            
            {/* Image - Mobile First, Desktop Right */}
            <div className="order-1 lg:order-2 lg:col-span-6">
              <div className="relative w-full max-w-[560px] mx-auto flex items-center justify-center h-80">
                <div className="w-full h-full flex items-center justify-center">
                  {/* Elegant Frame with Flow Connection */}
                  <div className="relative w-full h-full">
                    {/* Main frame container */}
                    <div className="relative w-full h-full bg-gradient-to-b from-[#FAF6F0] via-white to-[#E7F3E8] rounded-t-3xl rounded-b-none border-l-2 border-r-2 border-t-2 border-[#A3CFA2]/30 shadow-lg overflow-hidden">
                      {/* Connecting bottom flow */}
                      <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-b from-[#E7F3E8] to-transparent"></div>
                      
                      {/* Image container with elegant spacing */}
                      <div className="relative w-full h-full p-6 flex items-center justify-center">
                        {/* Subtle inner glow */}
                        <div className="absolute inset-4 bg-gradient-to-br from-[#A3CFA2]/8 to-[#9B8FC1]/8 rounded-2xl"></div>
                        
                        {/* Image with soft frame */}
                        <div className="relative z-10 w-full h-full flex items-center justify-center">
                          <img 
                            src="/materials/your-next-chapter-image-two.png" 
                            alt="Your Next Chapter" 
                            className="w-full h-full object-contain object-center filter drop-shadow-md"
                          />
                        </div>
                      </div>
                      
                      {/* Decorative flowing elements */}
                      <div className="absolute top-4 left-4 w-16 h-1 bg-gradient-to-r from-[#A3CFA2] to-transparent rounded-full opacity-40"></div>
                      <div className="absolute top-8 left-6 w-12 h-1 bg-gradient-to-r from-[#9B8FC1] to-transparent rounded-full opacity-30"></div>
                      <div className="absolute bottom-12 right-4 w-20 h-1 bg-gradient-to-l from-[#A3CFA2] to-transparent rounded-full opacity-35"></div>
                    </div>
                    
                    {/* Extended bottom connection for smooth flow */}
                    <div className="absolute -bottom-4 left-4 right-4 h-8 bg-gradient-to-b from-[#E7F3E8]/50 to-transparent rounded-b-2xl"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Content - Mobile Second, Desktop Left */}
            <div className="order-2 lg:order-1 lg:col-span-6">
              <h2 id="final-cta-title" className="text-3xl md:text-5xl font-bold text-neutral-900">Your Next Chapter Starts Here.</h2>
              <p className="mt-3 text-base md:text-lg text-neutral-700">Whether you're building your career or building your team, it starts with one click.</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/auth?role=job_seeker" aria-label="Start My Journey">
                  <Button size="lg" className="bg-[color:var(--nxr-green)] hover:bg-[#96c395] text-neutral-900 font-semibold shadow-sm">Start My Journey</Button>
                </Link>
                <Link to="/auth?role=employer" aria-label="Find My Next Hire">
                  <Button size="lg" className="bg-[color:var(--nxr-purple)] hover:bg-[#8e80c0] text-white font-semibold shadow-sm">Find My Next Hire</Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4 – Supported Positions */}
      <section id="positions" className="py-16 relative overflow-hidden scroll-mt-24" aria-labelledby="positions-title" style={{ background: 'linear-gradient(to bottom, #FFFFFF 0%, #F8F9FA 100%)' }}>
        <style>{`
          .position-card:hover {
            transform: translateY(-2px);
          }
          .position-icon {
            transition: all 0.3s ease;
          }
          .position-card:hover .position-icon {
            transform: scale(1.05);
          }
        `}</style>

        {/* Background decorative elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-10 top-20 w-32 h-32 bg-gradient-to-br from-[#A3CFA2]/10 to-transparent rounded-full blur-2xl"></div>
          <div className="absolute right-20 bottom-20 w-40 h-40 bg-gradient-to-br from-[#9B8FC1]/10 to-transparent rounded-full blur-2xl"></div>
        </div>

        <div className="relative mx-auto max-w-6xl px-6 relative z-10">
          <div className="text-center mb-10">
            <h2 id="positions-title" className="text-3xl md:text-4xl font-bold text-neutral-900">Healthcare Positions</h2>
            <p className="mt-2 text-lg text-neutral-600">Available through Nomera Health</p>
          </div>

          {/* Positions Grid - 3+2 Layout with Image Section */}
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 mb-12">
            {/* Left Side - Accessible Pill Tabs */}
            <div className="lg:w-1/2">
              <div className="space-y-5">
                <div role="tablist" aria-label="Position categories" className="flex flex-wrap gap-3">
                  {[
                    { key: 'nursing', label: 'Nursing' },
                    { key: 'allied', label: 'Allied Health' },
                    { key: 'diagnostic', label: 'Diagnostic & Lab' },
                    { key: 'pharmacy', label: 'Pharmacy & Support' },
                    { key: 'admin', label: 'Administrative' },
                  ].map(({ key, label }) => (
                    <button
                      key={key as string}
                      role="tab"
                      aria-selected={activeCategory === (key as any)}
                      onClick={() => setActiveCategory(key as any)}
                      className={`inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-medium border transition ${activeCategory === (key as any)
                        ? 'bg-white border-emerald-300 text-emerald-700 shadow-sm'
                        : 'bg-white border-gray-200 text-neutral-800 hover:border-gray-300'}`}
                    >
                      <span className={`h-2 w-2 rounded-full ${activeCategory === (key as any) ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                      {label}
                    </button>
                  ))}
                  </div>

                <div role="tabpanel" aria-labelledby={`${activeCategory}-tab`} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                  {activeCategory === 'nursing' && (
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-neutral-700 list-disc ml-4">
                      <li>Registered Nurses (RN)</li>
                      <li>Licensed Practical Nurses (LPN)</li>
                      <li>Certified Nursing Assistants (CNA)</li>
                      <li>Nurse Practitioners (NP)</li>
                    </ul>
                  )}
                  {activeCategory === 'allied' && (
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-neutral-700 list-disc ml-4">
                      <li>Medical Assistants (MA)</li>
                      <li>Physical Therapists (PT)</li>
                      <li>Occupational Therapists (OT)</li>
                      <li>Respiratory Therapists</li>
                    </ul>
                  )}
                  {activeCategory === 'diagnostic' && (
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-neutral-700 list-disc ml-4">
                      <li>Phlebotomists</li>
                      <li>Patient Care Technicians</li>
                      <li>Radiologic Technologists</li>
                      <li>Medical Technologists</li>
                    </ul>
                  )}
                  {activeCategory === 'pharmacy' && (
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-neutral-700 list-disc ml-4">
                      <li>Pharmacy Technicians</li>
                    </ul>
                  )}
                  {activeCategory === 'admin' && (
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-neutral-700 list-disc ml-4">
                      <li>Front Desk / Medical Receptionists</li>
                    </ul>
                  )}
                  </div>

                <div className="pt-1">
                  <Link to="/healthcare-professionals" className="text-emerald-700 hover:underline">Browse all positions</Link>
                        </div>
              </div>
            </div>

            {/* Right Side - Large Image Section */}
            <div className="lg:w-1/2 flex items-center justify-center lg:self-stretch">
              <div className="w-full max-w-lg h-72 sm:h-80 lg:h-[420px] bg-gradient-to-br from-[#A3CFA2]/5 to-[#9B8FC1]/5 rounded-3xl border border-gray-200 shadow-lg overflow-hidden flex items-center justify-center">
                <img 
                  src="/materials/smiling-nurse-positions.png" 
                  alt="Smiling healthcare professional representing our supported positions"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center">
            <div className="bg-gradient-to-r from-[#A3CFA2]/10 via-[#9B8FC1]/10 to-[#A3CFA2]/10 rounded-2xl p-8 border border-gray-200">
              <h3 className="text-2xl md:text-3xl font-bold text-neutral-900 mb-4">
                Ready to Find Your Next Opportunity?
              </h3>
              <p className="text-lg text-neutral-600 mb-6 max-w-2xl mx-auto">
                Whether you're a healthcare professional looking for your next role or a facility seeking qualified staff, 
                our platform connects you with the right opportunities across the Midwest.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/healthcare-professionals" aria-label="Browse Healthcare Positions">
                  <Button size="lg" className="bg-[color:var(--nxr-green)] hover:bg-[#96c395] text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300">
                    Browse Healthcare Positions
                  </Button>
                </Link>
                <Link to="/employers" aria-label="Post a Position">
                  <Button size="lg" variant="outline" className="border-[color:var(--nxr-purple)] text-[color:var(--nxr-purple)] hover:bg-[color:var(--nxr-purple)] hover:text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300">
                    Post a Position
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 5 – Real Stories. Real Impact. */}
      <section id="stories" className="py-24 relative overflow-hidden scroll-mt-24" aria-labelledby="stories-title" style={{ background: 'linear-gradient(to bottom, #FAF6F0 0%, #FFFFFF 100%)' }}>
        <style>{`
          @keyframes nxr-bubble-in{0%{opacity:0; transform:translateY(8px) scale(.98)}100%{opacity:1; transform:translateY(0) scale(1)}}
          .nxr-bubble{ animation: nxr-bubble-in .5s ease both }
          .nxr-speech:before{ content:''; position:absolute; bottom:-10px; left:2rem; width:18px; height:18px; background:inherit; border:1px solid rgba(0,0,0,.06); border-top:none; border-left:none; transform: rotate(45deg); box-shadow: 2px 2px 6px rgba(0,0,0,.02); }
          .nxr-paper{ background-image: radial-gradient(rgba(0,0,0,.03) 1px, transparent 1px); background-size: 6px 6px; opacity:.5 }
          @media(prefers-reduced-motion:reduce){ .nxr-bubble{ animation:none } }
        `}</style>
        {/* Ambient texture overlay */}
        <div className="absolute inset-0 pointer-events-none nxr-paper" />

        {/* Mode-aware decorative accents */}
        <div className="absolute inset-0 pointer-events-none">
          {activeRole === 'employer' ? (
            <svg className="absolute right-6 top-16 w-56 h-28 opacity-20" viewBox="0 0 300 150" fill="none" aria-hidden="true">
              <circle cx="20" cy="120" r="3" fill="#9B8FC1" />
              <circle cx="110" cy="90" r="3" fill="#9B8FC1" />
              <circle cx="200" cy="60" r="3" fill="#9B8FC1" />
              <circle cx="280" cy="30" r="3" fill="#9B8FC1" />
              <path d="M20 120 L110 90 L200 60 L280 30" stroke="#9B8FC1" strokeWidth="1.2" />
            </svg>
          ) : (
            <svg className="absolute left-6 top-16 w-56 h-24 opacity-20" viewBox="0 0 300 120" fill="none" aria-hidden="true">
              <path d="M0 110 C 60 60, 140 100, 220 40" stroke="#7FB17D" strokeWidth="1.5" />
            </svg>
          )}
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-10">
            <h2 id="stories-title" className="text-3xl md:text-5xl font-bold text-neutral-900">Real Stories. Real Impact.</h2>
            <p className="mt-3 text-base md:text-lg text-neutral-600">Every hire changes more than just a job — it shapes a life.</p>
          </div>

          {/* Carousel */}
          <div className="relative">
            <div
              ref={carouselRef}
              className="[scrollbar-width:none] [-ms-overflow-style:none] overflow-x-auto snap-x snap-mandatory"
              style={{ scrollBehavior: 'smooth' }}
              onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
              onTouchMove={(e) => {
                if (touchStartX.current == null || !carouselRef.current) return;
                const dx = e.touches[0].clientX - touchStartX.current;
                if (Math.abs(dx) > 40) {
                  if (dx > 0) handlePrev(); else handleNext();
                  touchStartX.current = e.touches[0].clientX;
                }
              }}
              aria-roledescription="carousel"
            >
              <div className="grid grid-flow-col auto-cols-[85%] sm:auto-cols-[48%] lg:auto-cols-[32%] gap-4 sm:gap-6 py-2" role="group">
                {(() => {
                  const testimonials = activeRole === 'employer' ? [
                    { name: 'Ava M., HR Director', role: 'Hospital System', text: 'We hired faster with better fit. The pipeline finally felt curated — not crowded.' },
                    { name: 'Marcus R., Clinical Manager', role: 'Outpatient Clinic', text: 'Pre-screened candidates saved our team weeks. We met people ready to make impact.' },
                    { name: 'Lena P., Talent Lead', role: 'Long-Term Care', text: "Quality over quantity. The matches understood our facility's needs from day one." },
                    { name: 'Diego S., Hiring Manager', role: 'Home Health', text: 'Communication was seamless. Interviews felt intentional and efficient.' },
                  ] : [
                    { name: 'Nia T., RN', role: 'Job Seeker', text: "Applications went out automatically — I focused on interviews. Best process I've had." },
                    { name: 'Jordan K., CNA', role: 'Job Seeker', text: 'The AI matches actually made sense. I felt seen for my skills, not just keywords.' },
                    { name: 'Priya D., LPN', role: 'Job Seeker', text: 'I got notified fast when employers were interested. Clarity at every step.' },
                    { name: 'Ethan V., MA', role: 'Job Seeker', text: 'It felt calm, not chaotic. I knew where I stood, and what came next.' },
                  ];
                  return testimonials.map((t, idx) => (
                    <div key={t.name+idx} ref={idx===0?firstCardRef:undefined} className="nxr-bubble snap-start relative bg-white/90 border border-black/5 rounded-2xl p-5 sm:p-6 shadow-sm nxr-speech">
                      <p className="text-neutral-800 leading-relaxed">"{t.text}"</p>
                      <div className="mt-4">
                        <p className="font-semibold text-neutral-900">{t.name}</p>
                        <p className="text-sm text-neutral-600">{t.role}</p>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
            {/* Navigation */}
            <div className="mt-4 flex items-center justify-center gap-3" aria-hidden="false">
              <button onClick={handlePrev} className="px-3 py-2 rounded-lg border border-black/10 bg-white/80 hover:bg-white text-neutral-800" aria-label="Previous testimonials">Prev</button>
              <button onClick={handleNext} className="px-3 py-2 rounded-lg border border-black/10 bg-white/80 hover:bg-white text-neutral-800" aria-label="Next testimonials">Next</button>
            </div>
          </div>

          {/* CTA Banner */}
          <div className="mt-10 sm:mt-12 rounded-2xl p-6 sm:p-8 text-center" style={{ background: 'linear-gradient(90deg, rgba(163,207,162,.25), rgba(255,255,255,.25), rgba(155,143,193,.25))' }}>
            <p className="text-lg md:text-xl text-neutral-800 mb-4">Your success story starts here.</p>
            {activeRole === 'employer' ? (
              <Link to="/auth" aria-label="Find My Next Hire">
                <Button size="lg" className="bg-[color:var(--nxr-purple)] hover:bg-[#8e80c0] text-white font-semibold shadow-sm">Find My Next Hire</Button>
              </Link>
            ) : (
              <Link to="/auth" aria-label="Start My Journey">
                <Button size="lg" className="bg-[color:var(--nxr-green)] hover:bg-[#96c395] text-neutral-900 font-semibold shadow-sm">Start My Journey</Button>
              </Link>
            )}
          </div>
        </div>
      </section>



      <Footer />

      {/* Emergency profile modal removed per request */}
    </div>
  );
}
